import * as React from "react";
import { FaqProps } from "./typings";
import { UitkCard, UitkCardTextSegment, UitkCardContent } from "uitk-react-cards";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import { inject } from "mobx-react";
import { UitkExpandoList, UitkExpandoListItem } from "uitk-react-expando";
import { sendDelayedTrackEvent, FlexTrackingInfo, Action } from "src/components/utility/analytics/FlexAnalyticsUtils";
import { AnalyticsStore } from "bernie-core";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading } from "uitk-react-text";

/**
 * Frequently asked questions module that allows users to gather
 * more information on their desired destinations
 */
export const Faq = inject(
  "markdown",
  "analytics"
)((props: FaqProps) => {
  const { flexComponent, markdown, analytics } = props;

  if (
    !markdown ||
    !analytics ||
    !flexComponent ||
    !flexComponent.model ||
    !flexComponent.model.faqs ||
    flexComponent.model.faqs.length === 0
  ) {
    return null;
  }

  const faqs = flexComponent.model.faqs;
  const structuredData = flexComponent.model.structuredData;
  const itemKeyHelper = new ItemKeyHelper("faq");
  const { formatText } = useLocalization();

  return (
    <div
      className="Faq uitk-cell all-cell-1-1 all-b-padding-three all-y-padding-three"
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <SchemaOrgScript structuredData={structuredData} />
      <UitkCard>
        <UitkCardContent>
          <UitkCardTextSegment key={itemKeyHelper.next()}>
            <UitkHeading level={2} size={600}>
              {formatText("frequentlyAskedQuestions.title")}
            </UitkHeading>
          </UitkCardTextSegment>
          <UitkExpandoList className="faqExpando">
            {faqs.map((faq, index) => (
              <Expando
                key={itemKeyHelper.next()}
                callToActionText={faq.question}
                expandedText={markdown.compileMarkdown(faq.question, faq.answer)}
                data-testid={`faq-${index}`}
                rfrr={`question.${index}`}
                analytics={analytics}
              />
            ))}
          </UitkExpandoList>
        </UitkCardContent>
      </UitkCard>
    </div>
  );
});

interface ExpandoProps extends React.HTMLAttributes<HTMLDivElement> {
  callToActionText: string;
  expandedText: string | undefined;
  rfrr: string;
  analytics: AnalyticsStore;
}
/**
 * This is a simple expander that when clicked, will show more text
 * TODO: Replace with UITK component when it has the design we need
 * @param callToActionText - Text that the user sees regardless of expansion
 * @param expandedText - Text that the user sees once expanded
 */
function Expando({ callToActionText, expandedText, rfrr, analytics }: ExpandoProps) {
  if (expandedText === undefined) {
    return null;
  }

  const [isVisible, setIsVisible] = React.useState(false);
  const callback = React.useCallback(() => {
    const flexTracking: FlexTrackingInfo = {
      moduleName: "faq",
      action: Action.CLICK,
      rfrr: `${rfrr}.${isVisible ? "close" : "open"}`,
    };
    sendDelayedTrackEvent(flexTracking, analytics);
    setIsVisible(!isVisible);
  }, [isVisible]);

  return (
    <UitkExpandoListItem
      isVisible={false}
      expandoTitle={callToActionText}
      expandoDescription={<div className="faqText" dangerouslySetInnerHTML={{ __html: expandedText }} />}
      analytics={{
        id: rfrr,
        callback,
      }}
    />
  );
}

export default Faq;
